(() => {
  const formatMoney = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 0
  }).format(Number(cents || 0) / 100);

  const imageUrl = image => {
    const source = typeof image === 'string' ? image : image?.src;
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', '180');
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const createProductItem = (product, currency) => {
    const variant = (product.variants || []).find(item => item.available) || product.variants?.[0];
    if (!variant) return null;

    const item = document.createElement('article');
    item.className = 'bb-product-cross-sell__item';
    item.dataset.bbCrossSellItem = 'true';
    item.dataset.productId = String(product.id);

    const imageLink = document.createElement('a');
    imageLink.className = 'bb-product-cross-sell__image-link';
    imageLink.href = product.url;
    imageLink.setAttribute('aria-label', `View ${product.title}`);
    const image = document.createElement('img');
    image.className = 'bb-product-cross-sell__image';
    image.src = imageUrl(product.featured_image || product.images?.[0]);
    image.alt = product.title;
    image.width = 180;
    image.height = 180;
    image.loading = 'lazy';
    imageLink.appendChild(image);

    const copy = document.createElement('div');
    copy.className = 'bb-product-cross-sell__copy';
    const title = document.createElement('a');
    title.className = 'bb-product-cross-sell__title';
    title.href = product.url;
    title.textContent = product.title;
    const price = document.createElement('div');
    price.className = 'bb-product-cross-sell__price';
    const sellingPrice = document.createElement('span');
    sellingPrice.textContent = formatMoney(variant.price, currency);
    price.appendChild(sellingPrice);
    if (Number(variant.compare_at_price || 0) > Number(variant.price || 0)) {
      const comparePrice = document.createElement('s');
      comparePrice.textContent = formatMoney(variant.compare_at_price, currency);
      price.appendChild(comparePrice);
    }
    copy.append(title, price);

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'bb-product-cross-sell__add';
    addButton.dataset.bbCrossSellAdd = 'true';
    addButton.dataset.variantId = String(variant.id);
    addButton.setAttribute('aria-label', `Add ${product.title} to cart`);
    addButton.disabled = !variant.available;
    addButton.textContent = variant.available ? 'Add' : 'Sold out';

    item.append(imageLink, copy, addButton);
    return item;
  };

  const fetchRecommendations = async (productId, excludedIds, needed) => {
    const root = window.Shopify?.routes?.root || '/';
    const matches = [];
    for (const intent of ['complementary', 'related']) {
      const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=${intent}`;
      const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) continue;
      const payload = await response.json();
      if (!Array.isArray(payload.products)) continue;
      payload.products.forEach(product => {
        const id = String(product.id);
        if (!product.available || excludedIds.has(id)) return;
        excludedIds.add(id);
        matches.push(product);
      });
      if (matches.length >= needed) break;
    }
    return matches.slice(0, needed);
  };

  const hydrateRecommendations = async root => {
    const list = root.querySelector('[data-bb-cross-sell-list]');
    const status = root.querySelector('[data-bb-cross-sell-status]');
    const maxItems = Number(root.dataset.maxItems || 2);
    if (!list || list.children.length >= maxItems || root.dataset.autoFill !== 'true') {
      if (status) status.textContent = '';
      return;
    }

    try {
      const excludedIds = new Set([
        String(root.dataset.productId || ''),
        ...[...list.querySelectorAll('[data-product-id]')].map(item => String(item.dataset.productId || ''))
      ]);
      const needed = maxItems - list.children.length;
      const recommendations = await fetchRecommendations(root.dataset.productId, excludedIds, needed);
      recommendations
        .forEach(product => {
          const item = createProductItem(product, root.dataset.currency);
          if (item) list.appendChild(item);
        });
      root.hidden = list.children.length === 0;
      if (status) status.textContent = '';
    } catch (error) {
      root.hidden = list.children.length === 0;
      if (status) status.textContent = list.children.length ? '' : 'Matching lights are temporarily unavailable.';
    }
  };

  const addRecommendedProduct = async (root, button) => {
    const status = root.querySelector('[data-bb-cross-sell-status]');
    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    if (status) status.textContent = '';

    try {
      const shopRoot = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] })
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.description || 'Could not add this light.');
      }
      await response.json();
      const cartResponse = await fetch(`${shopRoot}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' });
      const cart = cartResponse.ok ? await cartResponse.json() : null;
      document.dispatchEvent(new CustomEvent('bb:cart-updated', { detail: { cart, openDrawer: true } }));
      button.textContent = 'Added';
      if (status) status.textContent = 'Added to your cart.';
      if (window.BbOpenCartDrawer) await window.BbOpenCartDrawer();
    } catch (error) {
      button.disabled = false;
      button.textContent = originalLabel;
      if (status) status.textContent = error.message || 'Could not add this light. Please try again.';
      return;
    }

    window.setTimeout(() => {
      button.disabled = false;
      button.textContent = originalLabel;
    }, 1600);
  };

  const initialize = root => {
    if (root.dataset.bbCrossSellInitialized === 'true') return;
    root.dataset.bbCrossSellInitialized = 'true';
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-bb-cross-sell-add]');
      if (button && !button.disabled) addRecommendedProduct(root, button);
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        hydrateRecommendations(root);
      }, { rootMargin: '300px 0px' });
      observer.observe(root);
    } else {
      hydrateRecommendations(root);
    }
  };

  const initializeAll = scope => scope.querySelectorAll('[data-bb-product-cross-sell]').forEach(initialize);
  initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
})();
